import { useCallback, useEffect, useRef, useState } from 'react';

import type { ExtensionExecutionContext, ExtensionWithFields } from '$src/lib/extensions/types';
import type { CheckoutExtension, ExtensionFieldId } from '@getjust/checkout-extensions';

import { useCart, useShop } from '$src/hooks/queries';
import { useROEmailAtom } from '$src/hooks/state';
import { ExtensionManager } from '$src/lib/extensions/core/ExtensionManager';

/**
 * React hook to use the ExtensionManager
 */
export function useExtensionManager(extensions: CheckoutExtension[]) {
  const managerRef = useRef<ExtensionManager>(new ExtensionManager());
  const { data: cart } = useCart();
  const { data: shop } = useShop();
  const email = useROEmailAtom();

  // State to trigger re-renders when field values change
  const [fieldValues, setFieldValues] = useState<Record<ExtensionFieldId, unknown>>({});

  // Initialize manager
  if (!managerRef.current) {
    managerRef.current = new ExtensionManager();
  }

  const manager = managerRef.current;

  // Load extensions when they change - ultra simplified
  useEffect(() => {
    manager.loadExtensions(extensions);

    // Initialize field defaults from visible fields
    const visibleFields = manager.getVisibleFields({
      cartId: cart?.id || '',
      shopId: shop?.id || '',
      email: email || undefined,
      fieldValues: {},
    });

    const defaults: Record<ExtensionFieldId, unknown> = {};
    for (const field of visibleFields) {
      if (field.type === 'checkbox' && fieldValues[field.id] === undefined) {
        defaults[field.id] = field.defaultChecked ?? false;
      }
    }

    if (Object.keys(defaults).length > 0) {
      setFieldValues((prev) => ({ ...prev, ...defaults }));
    }
  }, [extensions, manager, cart?.id, shop?.id, email, fieldValues]);

  // Build execution context
  const buildContext = useCallback(
    (additionalContext?: Partial<ExtensionExecutionContext>): ExtensionExecutionContext => {
      return {
        cartId: cart?.id || '',
        shopId: shop?.id || '',
        email: email || undefined,
        fieldValues: { ...fieldValues },
        ...additionalContext,
      };
    },
    [cart?.id, shop?.id, email, fieldValues],
  );

  // Set field value
  const setFieldValue = useCallback((fieldId: ExtensionFieldId, value: unknown) => {
    console.warn(`[useExtensionManager] Setting field ${fieldId} to:`, value);
    setFieldValues((prev) => ({ ...prev, [fieldId]: value }));
  }, []);

  // Get field value
  const getFieldValue = useCallback(
    (fieldId: ExtensionFieldId): unknown => {
      return fieldValues[fieldId];
    },
    [fieldValues],
  );

  // Get extensions by context
  const getExtensionsByContext = useCallback(
    (context: string) => {
      return manager.getExtensionsByContext(context);
    },
    [manager],
  );

  // Execute actions directly
  const executeActions = useCallback(
    (trigger: string, additionalContext?: Partial<ExtensionExecutionContext>) => {
      const context = buildContext(additionalContext);
      return manager.executeActions(trigger, context);
    },
    [buildContext, manager],
  );

  // Get extensions grouped with their fields for rendering
  const getExtensionsWithFields = useCallback(
    (additionalContext?: Partial<ExtensionExecutionContext>) => {
      const context = buildContext(additionalContext);
      return manager.getExtensionsWithFields(context);
    },
    [buildContext, manager],
  );

  // Evaluate conditions
  const evaluateConditions = useCallback(
    (conditions: any, additionalContext?: Partial<ExtensionExecutionContext>) => {
      const context = buildContext(additionalContext);
      return manager.evaluateConditions(conditions, context);
    },
    [buildContext, manager],
  );

  return {
    // Core manager
    manager,

    fieldValues,
    setFieldValue,
    getFieldValue,

    getExtensionsWithFields,
    evaluateConditions,
    getExtensionsByContext,

    executeActions,
  };
}
